import * as React from 'react';
import { LoginIFrame } from './frame';
import { Dialog } from '@dx-ui/osc-dialog-v2';
import type { GuestLoginConfigOptions, LoginResponse } from './login.types';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import { sendReward } from '@dx-ui/framework-conductrics';
import unset from 'lodash/unset';
import { Button } from '@dx-ui/global-ui-prism';

export type LoginBaseProps = {
  /** Source url for the login page */
  frameSrc: string;
  /** Name of the button to display */
  buttonName?: string;
  /**
   * Set the classNames for the button element
   */
  buttonClassName?: string;
  /** Specify the title for the iframe element */
  title: string;
  /** Display options for the login page */
  options?: GuestLoginConfigOptions;
  /** Provides additional styling to the iframe */
  className?: string;
  /** Provides a parameter for disabling the icon */
  hideIcon?: boolean;
  /** callback to run after login attempt */
  onLoginAttempt: (response: LoginResponse) => Promise<void>;
  /** function to run on open */
  onOpen?: () => void;
};

export type LoginProps = LoginBaseProps & React.HTMLAttributes<HTMLIFrameElement>;

/**
 * A modal that wraps the LoginIFrame component.
 *
 * `import { Login } from '@dx-ui/osc-login'`
 */
export const Login: React.FC<React.PropsWithChildren<LoginBaseProps>> = ({
  options = {},
  className = '',
  title,
  frameSrc,
  buttonName,
  buttonClassName,
  hideIcon,
  onLoginAttempt,
  onOpen,
}) => {
  const [t] = useTranslation('osc-login');
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const open = () => {
    onOpen?.();
    sendReward('nav-sign-in-click');
    setShowDialog(true);
  };
  const close = () => {
    unset(window.digitalData, 'click');
    setShowDialog(false);
  };
  const handleLogin = async (response: LoginResponse) => {
    await onLoginAttempt?.(response);
    if (!response.error) {
      close();
    }
  };

  return (
    <Dialog
      title={title}
      isOpen={showDialog}
      onDismiss={close}
      size="md"
      dialogTrigger={
        <Button
          fill="flat"
          label={buttonName || t('signIn')}
          className={cx(
            'group font-semibold text-surface !no-underline brand-wa:!font-normal',
            buttonClassName
          )}
          onClick={open}
          iconProps={[
            {
              name: 'user-circle',
              size: 'md',
              variant: 'regular',
              align: 'after',
              className: cx({ hidden: hideIcon }, 'group-hover:hidden', 'group-focus:hidden'),
            },
            {
              name: 'user-circle',
              size: 'md',
              variant: 'solid',
              align: 'after',
              className: cx('hidden', {
                'group-hover:block': !hideIcon,
                'group-focus:block': !hideIcon,
              }),
            },
          ]}
        />
      }
    >
      <LoginIFrame
        frameSrc={frameSrc}
        options={options}
        data-e2e="loginIframe"
        onLoginAttempt={handleLogin}
        onClose={close}
        title={title}
        className={className}
      />
    </Dialog>
  );
};
